// =============================================================================
// PAGES/ADMINCHECKLISTETAPA.TSX — configurar checklist por etapa (2 níveis)
// =============================================================================
// Contextual por pipeline → etapa → GRUPOS → ITENS (espelho do Ploomes). Escolhe
// o funil, a etapa, e edita os grupos (nome, origem aplicável, ativo) e seus itens
// (nome, obrigatório, ativo). Importado do Ploomes; aqui o admin mantém.
// =============================================================================
import { useEffect, useState, useCallback } from 'react';
import { ListChecks, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { pipelinesApi, type PipelineBoard, type ChecklistGrupoParam } from '../services/api';

const ORIGENS: Array<{ v: string; label: string }> = [
  { v: '', label: 'Todas' }, { v: 'QUINTO_ANDAR', label: '5A' }, { v: 'S_QUATRO', label: 'S4' }, { v: 'VAREJO', label: 'VAR' },
];

export default function AdminChecklistEtapa() {
  const [board, setBoard] = useState<PipelineBoard[] | null>(null);
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [estagioId, setEstagioId] = useState<string | null>(null);
  const [grupos, setGrupos] = useState<ChecklistGrupoParam[] | null>(null);
  const [novoGrupo, setNovoGrupo] = useState('');

  useEffect(() => {
    pipelinesApi.getBoard().then((b) => { setBoard(b); setPipelineId((p) => p ?? b[0]?.id ?? null); }).catch(() => toast.error('Falha ao carregar pipelines.'));
  }, []);
  const pipeline = board?.find((p) => p.id === pipelineId) ?? null;
  useEffect(() => { setEstagioId(pipeline?.estagios[0]?.id ?? null); }, [pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  const carregar = useCallback((eid: string) => {
    setGrupos(null);
    pipelinesApi.listarChecklistsEstagio(eid).then(setGrupos).catch(() => setGrupos([]));
  }, []);
  useEffect(() => { if (estagioId) carregar(estagioId); }, [estagioId, carregar]);

  // ── Grupos ──
  const patchGrupo = async (id: string, data: Partial<ChecklistGrupoParam>) => {
    setGrupos((gs) => gs?.map((g) => g.id === id ? { ...g, ...data } : g) ?? gs);
    try { await pipelinesApi.atualizarGrupo(id, data); } catch { toast.error('Não foi possível salvar.'); if (estagioId) carregar(estagioId); }
  };
  const removerGrupo = async (id: string) => {
    if (!confirm('Remover este checklist (grupo) e seus itens?')) return;
    try { await pipelinesApi.excluirGrupo(id); setGrupos((gs) => gs?.filter((g) => g.id !== id) ?? gs); } catch { toast.error('Falha ao remover.'); }
  };
  const addGrupo = async () => {
    if (!estagioId || !novoGrupo.trim()) return;
    try { const g = await pipelinesApi.criarGrupo({ estagioPipelineId: estagioId, nome: novoGrupo.trim(), ordem: (grupos?.length ?? 0) + 1 }); setGrupos((gs) => [...(gs ?? []), { ...g, itens: [] }]); setNovoGrupo(''); }
    catch { toast.error('Falha ao criar checklist.'); }
  };

  // ── Itens ──
  const patchItem = async (grupoId: string, itemId: string, data: { nome?: string; obrigatorio?: boolean; ativo?: boolean }) => {
    setGrupos((gs) => gs?.map((g) => g.id === grupoId ? { ...g, itens: g.itens.map((i) => i.id === itemId ? { ...i, ...data } : i) } : g) ?? gs);
    try { await pipelinesApi.atualizarItem(itemId, data); } catch { toast.error('Não foi possível salvar o item.'); if (estagioId) carregar(estagioId); }
  };
  const removerItem = async (grupoId: string, itemId: string) => {
    try { await pipelinesApi.excluirItem(itemId); setGrupos((gs) => gs?.map((g) => g.id === grupoId ? { ...g, itens: g.itens.filter((i) => i.id !== itemId) } : g) ?? gs); }
    catch { toast.error('Falha ao remover item.'); }
  };
  const addItem = async (grupoId: string, nome: string, ordem: number) => {
    if (!nome.trim()) return;
    try { const it = await pipelinesApi.criarItem({ checklistEtapaId: grupoId, nome: nome.trim(), ordem: ordem + 1 }); setGrupos((gs) => gs?.map((g) => g.id === grupoId ? { ...g, itens: [...g.itens, it] } : g) ?? gs); }
    catch { toast.error('Falha ao criar item.'); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-1"><ListChecks size={22} className="text-otm-orange" /><h1 className="text-xl font-bold text-otm-txt-1">Checklist por etapa</h1></div>
      <p className="text-sm text-otm-txt-3 mb-4">Por funil → etapa, configure os grupos de checklist e seus itens que a operação precisa cumprir para avançar.</p>

      {!board ? (
        <div className="flex items-center gap-2 text-otm-txt-3 py-10 justify-center"><Loader2 size={18} className="animate-spin" /> Carregando…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            {board.map((p) => (
              <button key={p.id} onClick={() => setPipelineId(p.id)} className={`px-3 py-1.5 rounded-full text-[13px] font-medium border ${p.id === pipelineId ? 'bg-otm-navy text-white border-otm-navy' : 'bg-white text-otm-txt-2 border-otm-border hover:bg-otm-subtle'}`}>{p.nome}</button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
            <div className="bg-white rounded-xl border border-otm-border p-2 h-fit">
              {pipeline?.estagios.map((e) => (
                <button key={e.id} onClick={() => setEstagioId(e.id)} className={`w-full text-left text-[13px] px-2.5 py-1.5 rounded-lg truncate ${e.id === estagioId ? 'bg-otm-navy/10 text-otm-navy font-semibold' : 'text-otm-txt-2 hover:bg-otm-subtle'}`} title={e.nome}>{e.nome}</button>
              ))}
            </div>

            <div className="space-y-3">
              {grupos === null ? (
                <div className="flex items-center gap-2 text-otm-txt-3 py-6 justify-center bg-white rounded-xl border border-otm-border"><Loader2 size={16} className="animate-spin" /> Carregando…</div>
              ) : (
                <>
                  {grupos.length === 0 && <p className="text-sm text-otm-txt-3 bg-white rounded-xl border border-otm-border p-4">Nenhum checklist neste estágio.</p>}
                  {grupos.map((g) => <GrupoCard key={g.id} grupo={g} onPatch={patchGrupo} onRemove={removerGrupo} onPatchItem={patchItem} onRemoveItem={removerItem} onAddItem={addItem} />)}
                  <div className="flex items-center gap-2 bg-white rounded-xl border border-dashed border-otm-border p-3">
                    <input value={novoGrupo} onChange={(e) => setNovoGrupo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addGrupo(); }} placeholder="Novo checklist (grupo)…" disabled={!estagioId} className="flex-1 text-[13px] border border-otm-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-otm-navy/20" />
                    <button onClick={addGrupo} disabled={!novoGrupo.trim()} className="inline-flex items-center gap-1 text-[13px] font-semibold text-white bg-otm-navy px-3 py-1.5 rounded-lg disabled:opacity-40"><Plus size={14} /> Checklist</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function GrupoCard({ grupo, onPatch, onRemove, onPatchItem, onRemoveItem, onAddItem }: {
  grupo: ChecklistGrupoParam;
  onPatch: (id: string, d: Partial<ChecklistGrupoParam>) => void;
  onRemove: (id: string) => void;
  onPatchItem: (gid: string, iid: string, d: { nome?: string; obrigatorio?: boolean; ativo?: boolean }) => void;
  onRemoveItem: (gid: string, iid: string) => void;
  onAddItem: (gid: string, nome: string, ordem: number) => void;
}) {
  const [novoItem, setNovoItem] = useState('');
  return (
    <div className={`bg-white rounded-xl border p-3 ${grupo.ativo ? 'border-otm-border' : 'border-dashed border-otm-border bg-otm-subtle/40'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input defaultValue={grupo.nome} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== grupo.nome) onPatch(grupo.id, { nome: e.target.value.trim() }); }} className="flex-1 min-w-[160px] text-[13px] font-semibold text-otm-txt-1 bg-transparent border-b border-transparent focus:border-otm-border focus:outline-none py-0.5" />
        <select value={grupo.origemAplicavel ?? ''} onChange={(e) => onPatch(grupo.id, { origemAplicavel: e.target.value || null })} className="text-[12px] border border-otm-border rounded px-1.5 py-1 bg-white">
          {ORIGENS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <label className="text-[12px] text-otm-txt-2 inline-flex items-center gap-1"><input type="checkbox" checked={grupo.ativo} onChange={(e) => onPatch(grupo.id, { ativo: e.target.checked })} /> ativo</label>
        <button onClick={() => onRemove(grupo.id)} title="Remover checklist" className="text-otm-txt-3 hover:text-red-600"><Trash2 size={15} /></button>
      </div>
      {/* Itens */}
      <div className="mt-2 pl-2 border-l-2 border-otm-subtle space-y-1.5">
        {grupo.itens.length === 0 && <p className="text-[12px] text-otm-txt-3">Sem itens.</p>}
        {grupo.itens.map((it) => (
          <div key={it.id} className="flex items-center gap-2">
            <input defaultValue={it.nome} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== it.nome) onPatchItem(grupo.id, it.id, { nome: e.target.value.trim() }); }} className="flex-1 text-[13px] text-otm-txt-2 bg-transparent border-b border-transparent focus:border-otm-border focus:outline-none py-0.5" />
            <label className="text-[11px] text-otm-txt-3 inline-flex items-center gap-1"><input type="checkbox" checked={it.obrigatorio} onChange={(e) => onPatchItem(grupo.id, it.id, { obrigatorio: e.target.checked })} /> obrigatório</label>
            <button onClick={() => onRemoveItem(grupo.id, it.id)} title="Remover item" className="text-otm-txt-3 hover:text-red-600"><Trash2 size={13} /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1">
          <input value={novoItem} onChange={(e) => setNovoItem(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { onAddItem(grupo.id, novoItem, grupo.itens.length); setNovoItem(''); } }} placeholder="Novo item…" className="flex-1 text-[12px] border border-otm-border rounded px-2 py-1 focus:outline-none" />
          <button onClick={() => { onAddItem(grupo.id, novoItem, grupo.itens.length); setNovoItem(''); }} disabled={!novoItem.trim()} className="inline-flex items-center gap-1 text-[12px] text-otm-navy font-medium px-2 py-1 rounded hover:bg-otm-navy/5 disabled:opacity-40"><Plus size={13} /> item</button>
        </div>
      </div>
    </div>
  );
}
